import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import composioService from '../../services/composioService';
import { describeApiError } from '../../utils/apiError';
import { isOAuthPopupWindow, publishOAuthResult } from '../../utils/composioOAuthPopup';

/**
 * OAuth Callback Page
 * Handles OAuth callbacks from Composio. In the same-tab flow it redirects back
 * to the tools page; in the one-click popup flow it reports the result to the
 * waiting dialog and closes itself.
 */
const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing OAuth callback...');
  const [isPopup] = useState(isOAuthPopupWindow);

  useEffect(() => {
    let cancelled = false;
    const timers = [];

    const finish = (outcome, text) => {
      if (cancelled) return;
      setStatus(outcome);
      setMessage(text);

      if (isPopup) {
        publishOAuthResult({ status: outcome, message: text });
        // Leave an error up a little longer; the dialog shows it too.
        timers.push(setTimeout(() => window.close(), outcome === 'success' ? 1500 : 4000));
        return;
      }

      const target = outcome === 'success' ? '/configure/tools?oauth_success=true' : '/configure/tools?oauth_error=true';
      timers.push(setTimeout(() => navigate(target, { replace: true }), outcome === 'success' ? 2000 : 3000));
    };

    const processCallback = async () => {
      const params = Object.fromEntries(searchParams.entries());
      const { status: returnStatus, appName } = params;
      const providerError = params.error_description || params.error;

      console.log('OAuth callback parameters:', params);

      if (returnStatus === 'error' || returnStatus === 'failure' || providerError) {
        finish(
          'error',
          providerError
            ? `OAuth authentication failed: ${providerError}`
            : 'OAuth authentication failed. Please try again.'
        );
        return;
      }

      setMessage(`Processing connection to ${appName || 'the app'}...`);
      try {
        const result = await composioService.handleOAuthCallback(params);
        console.log('Backend callback result:', result);

        const succeeded =
          returnStatus === 'success' || result?.success || result?.status === 'success' || result?.connected;
        if (succeeded) {
          finish('success', `Successfully connected to ${appName || result?.app_name || 'the app'}!`);
        } else {
          finish('error', result?.message || 'OAuth authentication failed.');
        }
      } catch (error) {
        console.error('Error calling backend callback:', error);
        finish('error', describeApiError(error, 'Failed to process OAuth callback. Please try again.'));
      }
    };

    processCallback();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [searchParams, navigate, isPopup]);

  const followUp = isPopup ? 'You can close this window.' : 'Redirecting you back to the tools page...';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
        textAlign: 'center',
      }}
    >
      {status === 'processing' && (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            {message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we process your OAuth authentication...
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
            {message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {followUp}
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <Error sx={{ fontSize: 60, color: 'error.main', mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
            {message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {followUp}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthCallback;
